import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

interface ThreadFormProps {
  categoryId?: string
  onSubmit: (threadData: ThreadFormData) => void
  onCancel: () => void
  isSubmitting?: boolean
}

interface ThreadFormData {
  title: string
  content: string
  categoryId: string
  tags: string[]
  isPinned?: boolean
}

const forumCategories = [
  { id: 'general', name: 'General Discussion', requiredRole: null },
  { id: 'vendor-discussion', name: 'Vendor Discussion', requiredRole: null },
  { id: 'strain-reviews', name: 'Strain Reviews', requiredRole: null },
  { id: 'dosing-help', name: 'Dosing & Effects', requiredRole: null },
  { id: 'verified-vendors', name: 'Verified Vendor Zone', requiredRole: 'verified_vendor' },
  { id: 'trading-post', name: 'Trading Post', requiredRole: null },
  { id: 'off-topic', name: 'Off Topic', requiredRole: null }
]

export default function ThreadForm({ categoryId, onSubmit, onCancel, isSubmitting = false }: ThreadFormProps) {
  const { user } = useAuth()
  const [formData, setFormData] = useState<ThreadFormData>({
    title: '',
    content: '',
    categoryId: categoryId || 'general',
    tags: [],
    isPinned: false
  })
  const [tagInput, setTagInput] = useState('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  const canAccessCategory = (category: any) => {
    if (!category.requiredRole) return true
    return user?.role === category.requiredRole || user?.role === 'moderator' || user?.role === 'admin'
  }

  const availableCategories = forumCategories.filter(canAccessCategory)

  const handleInputChange = (field: keyof ThreadFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase()
    if (tag && !formData.tags.includes(tag) && formData.tags.length < 5) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }))
      setTagInput('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({ 
      ...prev, 
      tags: prev.tags.filter(tag => tag !== tagToRemove) 
    }))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.target === document.querySelector('.tag-input')) {
      e.preventDefault()
      addTag()
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Thread title is required'
    } else if (formData.title.length < 5) {
      newErrors.title = 'Title must be at least 5 characters long'
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be less than 200 characters'
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Thread content is required'
    } else if (formData.content.length < 10) {
      newErrors.content = 'Content must be at least 10 characters long'
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Please select a category'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      onSubmit(formData)
    }
  }

  const canPin = user?.role === 'moderator' || user?.role === 'admin'

  return (
    <div className="thread-form">
      <div className="form-header">
        <h2>Create New Thread</h2>
        <p>Share your thoughts, questions, or insights with the community</p>
      </div>

      <form onSubmit={handleSubmit} className="thread-form-content">
        {/* Category Selection */}
        <div className="form-group">
          <label htmlFor="category">Category</label>
          <select
            id="category"
            value={formData.categoryId}
            onChange={(e) => handleInputChange('categoryId', e.target.value)}
            className={errors.categoryId ? 'error' : ''}
          >
            <option value="">Select a category...</option>
            {availableCategories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
                {category.requiredRole && ' (Verified Vendors Only)'}
              </option>
            ))}
          </select>
          {errors.categoryId && <span className="error-message">{errors.categoryId}</span>}
        </div>

        {/* Thread Title */}
        <div className="form-group">
          <label htmlFor="title">Thread Title</label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="What's your thread about?"
            maxLength={200}
            className={errors.title ? 'error' : ''}
          />
          <div className="input-meta">
            <span className="char-count">{formData.title.length}/200</span>
          </div>
          {errors.title && <span className="error-message">{errors.title}</span>}
        </div>

        {/* Thread Content */}
        <div className="form-group">
          <label htmlFor="content">Content</label>
          <textarea
            id="content"
            value={formData.content}
            onChange={(e) => handleInputChange('content', e.target.value)}
            placeholder="Share your thoughts, experiences, or questions in detail..."
            rows={8}
            className={errors.content ? 'error' : ''}
          />
          <div className="input-meta">
            <span className="char-count">{formData.content.length} characters</span>
          </div>
          {errors.content && <span className="error-message">{errors.content}</span>}
        </div>

        {/* Tags */}
        <div className="form-group">
          <label htmlFor="tags">Tags (Optional)</label>
          <div className="tag-input-container">
            <input
              type="text"
              className="tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Add tags to help others find your thread..."
              maxLength={30}
            />
            <button 
              type="button" 
              onClick={addTag}
              className="add-tag-btn"
              disabled={!tagInput.trim() || formData.tags.length >= 5}
            >
              Add Tag
            </button>
          </div>
          
          {formData.tags.length > 0 && (
            <div className="tags-display">
              {formData.tags.map(tag => (
                <span key={tag} className="tag">
                  #{tag}
                  <button 
                    type="button" 
                    onClick={() => removeTag(tag)}
                    className="remove-tag"
                    aria-label={`Remove ${tag} tag`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="input-help">
            Add up to 5 tags to help categorize your thread (max 30 characters each)
          </div>
        </div>

        {/* Moderator Options */}
        {canPin && (
          <div className="form-group moderator-options">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isPinned}
                onChange={(e) => handleInputChange('isPinned', e.target.checked)}
              />
              <span className="checkmark"></span>
              Pin this thread (moderator only)
            </label>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button 
            type="button" 
            onClick={onCancel}
            className="btn-secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating Thread...' : 'Create Thread'}
          </button>
        </div>
      </form>

      {/* Guidelines */}
      <div className="form-guidelines">
        <h3>Community Guidelines</h3>
        <ul>
          <li>Be respectful and constructive in your discussions</li>
          <li>Stay on topic for the selected category</li>
          <li>Use clear, descriptive titles</li>
          <li>Search existing threads before creating new ones</li>
          <li>Follow all community rules and guidelines</li>
        </ul>
      </div>
    </div>
  )
}